import { ExerciseType } from "@hulanbv/platformapp";
import { ButtonHTMLAttributes, FC, useMemo, useState } from "react";
import { style } from "typestyle";
import { dictionary } from "../../state/common/constants/dictionary.constants";
import { exerciseIcons } from "../../state/common/constants/exercise-icons.constants";
import { InjectionService } from "../../state/common/injection.service";
import { exerciseColors } from "../../constants/exercise-colors.constant";
import { Button } from "./button.element";

interface IProps {
  attributes?: ButtonHTMLAttributes<HTMLButtonElement>;
  exerciseType: ExerciseType;
  link: string;

  /**
   * Allows modification of the link before opening it.
   * The link is passed as a parameter to the function and the modified link should be returned.
   * The function is called when the button is clicked, before opening the link.
   * @param link
   * @returns
   */
  finalizeLink?: (link: string) => string | Promise<string>;
}

export const ExerciseLinkButton: FC<IProps> = (props) => {
  const [buttonState, setButtonState] = useState<"idle" | "loading">("idle");

  const buttonText = useMemo(() => {
    const type = props.exerciseType;
    if (type === ExerciseType.GAME) {
      return dictionary.literals.goToGame;
    }
    if (type === ExerciseType.PDF) {
      return dictionary.literals.downloadPdf;
    }
    return dictionary.literals.goToLink;
  }, [props.exerciseType]);

  return (
    <Button
      attributes={{
        className: styles.button,
        onClick: async (evt) => {
          setButtonState("loading");
          try {
            if (props.attributes?.onClick) {
              props.attributes.onClick(evt);
            }

            const alteredLink =
              (await props.finalizeLink?.(props.link)) ?? props.link;
            InjectionService.openWebView(alteredLink);
          } finally {
            setButtonState("idle");
          }
        },
        style: {
          backgroundColor: exerciseColors[props.exerciseType],
          boxShadow: `0 4px 17px 0 rgba(${exerciseColors[
            props.exerciseType
          ].slice(4, -1)}, 0.5)`,
        },
        ...props.attributes,
        disabled: buttonState === "loading" || props.attributes?.disabled,
      }}
      icon={exerciseIcons[props.exerciseType]}
    >
      {buttonText}
    </Button>
  );
};

const styles = {
  button: style({
    textTransform: "uppercase",
    textAlign: "center",
  }),
};
